import { CANAIS, PRODUTOS, VOLUMES } from '../constants';
import type {
  CanalAquisicao,
  EmpresaForm,
  ProdutoId,
  RascunhoOnboardingSelf,
  VolumeCasosMes,
} from '../types';

// TODO: extrair wrapper genérico de storage JSON para shared/utils e migrar as 7 cópias (issue #737).
const CHAVE = 'onboardingSelf:rascunho';
const VERSAO_SCHEMA = 1;

const ehTexto = (valor: unknown): valor is string => typeof valor === 'string';

const lerEmpresa = (bruto: unknown): EmpresaForm | null => {
  if (typeof bruto !== 'object' || bruto === null) return null;
  const { cnpj, responsavelNome, responsavelEmail, termosAceitos } = bruto as Partial<
    Record<keyof EmpresaForm, unknown>
  >;
  if (!ehTexto(cnpj) || !ehTexto(responsavelNome) || !ehTexto(responsavelEmail)) return null;
  return { cnpj, responsavelNome, responsavelEmail, termosAceitos: termosAceitos === true };
};

const umDe = <T extends string>(valor: unknown, validos: readonly T[]): T | null =>
  validos.find((valido) => valido === valor) ?? null;

const IDS_PRODUTO = PRODUTOS.map((produto) => produto.id);
const IDS_VOLUME = VOLUMES.map((volume) => volume.id);
const IDS_CANAL = CANAIS.map((canal) => canal.id);

const salvar = (rascunho: RascunhoOnboardingSelf): void => {
  try {
    window.sessionStorage.setItem(CHAVE, JSON.stringify({ versao: VERSAO_SCHEMA, ...rascunho }));
  } catch {
    // Sem storage (aba anônima, cota) a pessoa só perde o preenchimento numa recarga.
    return;
  }
};

const ler = (): RascunhoOnboardingSelf | null => {
  let dados: unknown;
  try {
    const bruto = window.sessionStorage.getItem(CHAVE);
    if (!bruto) return null;
    dados = JSON.parse(bruto);
  } catch {
    return null;
  }
  if (typeof dados !== 'object' || dados === null) return null;

  const registro = dados as Record<string, unknown>;
  const empresa = lerEmpresa(registro.empresa);
  if (registro.versao !== VERSAO_SCHEMA || !empresa) return null;

  // Aceite sem a versão aceita não volta marcado: carimbá-lo com a versão vigente na
  // recarga provaria o aceite de um texto que a pessoa pode não ter visto.
  const termosVersaoAceita =
    empresa.termosAceitos && ehTexto(registro.termosVersaoAceita)
      ? registro.termosVersaoAceita
      : null;

  const produtos = Array.isArray(registro.produtos)
    ? registro.produtos
        .map((produto) => umDe<ProdutoId>(produto, IDS_PRODUTO))
        .filter((produto): produto is ProdutoId => produto !== null)
    : [];

  return {
    empresa: { ...empresa, termosAceitos: termosVersaoAceita !== null },
    produtos: [...new Set(produtos)],
    volume: umDe<VolumeCasosMes>(registro.volume, IDS_VOLUME),
    canal: umDe<CanalAquisicao>(registro.canal, IDS_CANAL),
    termosVersaoAceita,
  };
};

const limpar = (): void => {
  try {
    window.sessionStorage.removeItem(CHAVE);
  } catch {
    return;
  }
};

/**
 * Rascunho do wizard em `sessionStorage`: a recarga da aba volta com o que já foi
 * preenchido. A razão social não é guardada — é reconsultada na Receita.
 */
export const rascunhoOnboardingSelf = { salvar, ler, limpar };
